import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { StatusIcon, STATUS } from './statusUi';
import { cn } from '../../../utils/cn';

const ACTION_LABEL = {
  subscribe_app: 'Subscribe this app to the WABA',
  reactivate: 'Reactivate connection',
};

function ResultTable({ table }) {
  return (
    <div className="overflow-x-auto rounded border border-[#D8DDE6]">
      <table className="w-full text-[12px]">
        <thead className="bg-[#F4F6F9] text-[#706E6B]">
          <tr>{table.columns.map((c) => <th key={c} className="text-left font-bold px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#D8DDE6]">
          {table.rows.map((row, i) => (
            <tr key={i}>{row.map((cell, j) => <td key={j} className="px-3 py-2 align-top text-[#080707] break-words max-w-[360px]">{String(cell)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CheckRow({ check, defaultOpen = false, onAction, actionBusy, canReactivate, isAdmin }) {
  const hasMore = !!(check.details?.length || check.table || check.request || check.raw);
  const [open, setOpen] = useState(defaultOpen);
  const s = STATUS[check.status] || STATUS.skip;
  const showHint = check.hint && (check.status === 'fail' || check.status === 'warn' || check.status === 'skip');
  // "Reactivate" is only offered once the token itself is proven good, otherwise it would just fail again.
  const actionAllowed = check.action && (check.action !== 'reactivate' || canReactivate);

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="pt-0.5"><StatusIcon status={check.status} /></div>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="w-full text-left flex items-start justify-between gap-3 group"
            onClick={() => hasMore && setOpen((o) => !o)}
            aria-expanded={hasMore ? open : undefined}
            disabled={!hasMore}
          >
            <span className="min-w-0">
              <span className="block text-[13px] font-bold text-[#080707]">{check.title}</span>
              <span className={cn('block text-[12px] mt-0.5 break-words', check.status === 'fail' ? 'text-[#8E030F]' : check.status === 'warn' ? 'text-[#8C4B02]' : 'text-[#706E6B]')}>{check.summary}</span>
            </span>
            {hasMore && (
              <span className="flex items-center gap-1 text-[11px] text-[#706E6B] shrink-0 pt-0.5">
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="hidden sm:inline">{open ? 'Hide' : 'Details'}</span>
              </span>
            )}
          </button>

          {showHint && (
            <div className={cn('mt-2 rounded border px-3 py-2 text-[12px] text-[#080707]', s.soft)}>
              <span className="font-bold">{check.status === 'skip' ? 'To enable: ' : 'How to fix: '}</span>{check.hint}
            </div>
          )}

          {actionAllowed && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                icon={Wrench}
                loading={actionBusy === check.action}
                disabled={!isAdmin}
                title={isAdmin ? undefined : 'Only a company admin can do this'}
                onClick={() => onAction(check.action)}
              >
                {ACTION_LABEL[check.action] || check.action}
              </Button>
            </div>
          )}

          {open && hasMore && (
            <div className="mt-3 space-y-3">
              {check.details?.length > 0 && (
                <dl className="grid grid-cols-1 md:grid-cols-[minmax(160px,220px)_1fr] gap-x-4 gap-y-1.5 text-[12px]">
                  {check.details.map((d, i) => (
                    <React.Fragment key={i}>
                      <dt className="text-[#706E6B] font-semibold">{d.label}</dt>
                      <dd className="text-[#080707] break-words">{String(d.value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
              {check.table && <ResultTable table={check.table} />}
              {check.request && (
                <p className="text-[11px] font-mono text-[#706E6B] break-all">
                  {check.request.method} {check.request.path}
                  {check.request.httpStatus != null && <> → HTTP {check.request.httpStatus}</>}
                  {check.request.durationMs != null && <> · {check.request.durationMs} ms</>}
                  {check.request.fbtraceId && <> · fbtrace {check.request.fbtraceId}</>}
                </p>
              )}
              {check.raw && (
                <details>
                  <summary className="text-[11px] font-bold text-[#0176D3] cursor-pointer">Raw response from Meta</summary>
                  <pre className="mt-2 text-[11px] font-mono bg-[#0B0E14] text-[#E4E7EB] rounded-[4px] p-3 max-h-72 overflow-auto">{JSON.stringify(check.raw, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
